/**
 * task-form.ts — czysty model formularza zadania: stan nagłówka, hydratacja z odpowiedzi serwera
 * i budowa ciał żądań POST / PATCH (bez React, do testów).
 *
 * Daty: pola formularza trzymają wartość `datetime-local` (czas lokalny przeglądarki, bez strefy);
 * serwer przyjmuje i zwraca WYŁĄCZNIE ISO ze strefą. Przeliczenie: przy wczytaniu
 * `isoToLocalDateTimeInput`, przy zapisie `localDateTimeInputToIso` (`@/lib/local-date`).
 *
 * Firma zadania wynika z typu — ciało żądania NIGDY nie niesie `companyId`.
 */

import type { TaskPriority, TaskStatus } from '@saas/shared';

import { isoToLocalDateTimeInput, localDateTimeInputToIso } from '@/lib/local-date';
import type { TaskDetailDto } from '@/lib/tasks/task-api';
import { DEFAULT_TASK_PRIORITY, DEFAULT_TASK_STATUS } from '@/pages/tasks/task-model';

/** Pola dat zadania (klucze formularza = klucze ciała żądania). */
export const TASK_DATE_FIELDS = [
  'dueAt',
  'plannedStartAt',
  'plannedEndAt',
  'actualStartAt',
  'actualEndAt',
] as const;
export type TaskDateField = (typeof TASK_DATE_FIELDS)[number];

export interface TaskFormData {
  taskTypeId: string;
  /** Numer nadany przez serwer; `null` przed pierwszym zapisem. */
  systemNumber: string | null;
  title: string;
  description: string;
  status: TaskStatus;
  priority: TaskPriority;
  ownerUserId: string | null;
  /** Etykieta wybranej osoby (do zamkniętego pickera) — nie jest wysyłana. */
  ownerName: string | null;
  contractorId: string | null;
  /** Etykieta wybranego kontrahenta (do zamkniętego pickera) — nie jest wysyłana. */
  contractorName: string | null;
  categoryId: string | null;
  /** Autor zadania — tylko do odczytu, nie jest wysyłany. */
  createdByName: string | null;
  dueAt: string;
  plannedStartAt: string;
  plannedEndAt: string;
  actualStartAt: string;
  actualEndAt: string;
}

/**
 * Stan nowego zadania przed nałożeniem wartości domyślnych pól (nakłada je karta zadania):
 * status „Nowe", priorytet „Normalny", reszta pusta.
 */
export function emptyTaskForm(taskTypeId: string, createdByName: string | null): TaskFormData {
  return {
    taskTypeId,
    systemNumber: null,
    title: '',
    description: '',
    status: DEFAULT_TASK_STATUS,
    priority: DEFAULT_TASK_PRIORITY,
    ownerUserId: null,
    ownerName: null,
    contractorId: null,
    contractorName: null,
    categoryId: null,
    createdByName,
    dueAt: '',
    plannedStartAt: '',
    plannedEndAt: '',
    actualStartAt: '',
    actualEndAt: '',
  };
}

export function taskFormFromDetail(task: TaskDetailDto): TaskFormData {
  return {
    taskTypeId: task.taskTypeId,
    systemNumber: task.systemNumber,
    title: task.title,
    description: task.description ?? '',
    status: task.status,
    priority: task.priority,
    ownerUserId: task.ownerUserId,
    ownerName: task.ownerName,
    contractorId: task.contractorId,
    contractorName: task.contractorName,
    categoryId: task.categoryId,
    createdByName: task.createdByName,
    dueAt: isoToLocalDateTimeInput(task.dueAt),
    plannedStartAt: isoToLocalDateTimeInput(task.plannedStartAt),
    plannedEndAt: isoToLocalDateTimeInput(task.plannedEndAt),
    actualStartAt: isoToLocalDateTimeInput(task.actualStartAt),
    actualEndAt: isoToLocalDateTimeInput(task.actualEndAt),
  };
}

/** Pola-odwołania: identyfikator wysyłany do serwera + etykieta tylko do wyświetlenia w pickerze. */
const LABELLED_ID_FIELDS: ReadonlyArray<readonly [keyof TaskFormData, keyof TaskFormData]> = [
  ['ownerUserId', 'ownerName'],
  ['contractorId', 'contractorName'],
];

function copyField<K extends keyof TaskFormData>(
  target: TaskFormData,
  source: TaskFormData,
  key: K,
): void {
  target[key] = source[key];
}

/**
 * Formularz po zapisie odrzuconym konfliktem współbieżnym (jak w innych kartotekach — wpisane
 * zmiany zostają): pola zmienione przez użytkownika względem starej bazy zostają z formularza,
 * pozostałe (w tym typ i numer zmienione w międzyczasie przez kogoś innego) — z aktualnego stanu
 * serwera. Kolejny PATCH liczy różnicę od `fresh`, więc nie cofa cudzej zmiany typu.
 */
export function rebaseTaskForm(
  edited: TaskFormData,
  previousBase: TaskFormData,
  fresh: TaskFormData,
): TaskFormData {
  const next: TaskFormData = { ...fresh };
  const pairedLabelKeys = new Set<keyof TaskFormData>(LABELLED_ID_FIELDS.map(([, label]) => label));
  for (const key of Object.keys(fresh) as (keyof TaskFormData)[]) {
    if (pairedLabelKeys.has(key)) continue;
    if (edited[key] !== previousBase[key]) copyField(next, edited, key);
  }
  // Para id + etykieta to jedna jednostka: zmieniony przez użytkownika identyfikator niesie swoją
  // etykietę z formularza, niezmieniony bierze obie z serwera (etykieta nie rozjedzie się z id).
  for (const [idKey, labelKey] of LABELLED_ID_FIELDS) {
    if (edited[idKey] !== previousBase[idKey]) {
      copyField(next, edited, idKey);
      copyField(next, edited, labelKey);
    }
  }
  return next;
}

/**
 * Wartość pola daty → ISO ze strefą; `null` dla wartości niepoprawnej (nie do sparsowania albo z
 * rokiem poza 0000–9999, który serwer odrzuca). Pole puste też daje `null` — rozróżnia je wołający.
 */
function parseLocalDate(local: string): string | null {
  const iso = localDateTimeInputToIso(local);
  return iso !== null && /^\d{4}-/.test(iso) ? iso : null;
}

/** Pola dat z niepoprawną, niepustą wartością (np. rok > 9999, wklejony tekst). */
export function invalidTaskDateFields(form: TaskFormData): TaskDateField[] {
  return TASK_DATE_FIELDS.filter(
    (field) => form[field] !== '' && parseLocalDate(form[field]) === null,
  );
}

/**
 * Data z pola formularza → ISO ze strefą albo `null` (pole puste). Wartość niepoprawna rzuca —
 * ostatnia linia obrony; strona sprawdza `invalidTaskDateFields` i pokazuje błąd przy polu wcześniej.
 */
function dateToIso(field: TaskDateField, local: string): string | null {
  if (local === '') return null;
  const iso = parseLocalDate(local);
  if (iso === null) throw new Error(`Niepoprawna data w polu ${field}: „${local}"`);
  return iso;
}

/**
 * Ciało POST — typ + wypełnione pola. Puste pola opcjonalne są pomijane (serwer ustawia `null`),
 * status i priorytet idą zawsze.
 */
export function buildTaskCreateBody(form: TaskFormData): Record<string, unknown> {
  const body: Record<string, unknown> = {
    taskTypeId: form.taskTypeId,
    title: form.title.trim(),
    status: form.status,
    priority: form.priority,
  };
  if (form.description.trim() !== '') body.description = form.description;
  if (form.ownerUserId !== null) body.ownerUserId = form.ownerUserId;
  if (form.contractorId !== null) body.contractorId = form.contractorId;
  if (form.categoryId !== null) body.categoryId = form.categoryId;
  for (const field of TASK_DATE_FIELDS) {
    const iso = dateToIso(field, form[field]);
    if (iso !== null) body[field] = iso;
  }
  return body;
}

/**
 * Ciało PATCH — wyłącznie pola ZMIENIONE względem stanu z serwera (`original`). Dzięki temu zapis
 * nie przepisuje dat, których użytkownik nie ruszył (formularz zna je z dokładnością do minuty,
 * serwer — do sekundy), a serwer waliduje tylko to, co się zmieniło. Wyczyszczone pole → `null`.
 */
export function buildTaskPatchBody(
  form: TaskFormData,
  original: TaskFormData,
): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (form.title.trim() !== original.title) body.title = form.title.trim();
  if (form.description !== original.description) {
    body.description = form.description.trim() === '' ? null : form.description;
  }
  if (form.status !== original.status) body.status = form.status;
  if (form.priority !== original.priority) body.priority = form.priority;
  if (form.ownerUserId !== original.ownerUserId) body.ownerUserId = form.ownerUserId;
  if (form.contractorId !== original.contractorId) body.contractorId = form.contractorId;
  if (form.categoryId !== original.categoryId) body.categoryId = form.categoryId;
  for (const field of TASK_DATE_FIELDS) {
    if (form[field] !== original[field]) body[field] = dateToIso(field, form[field]);
  }
  return body;
}
